/**
 * Pure helpers for the Team page's "Team activity" tab (no React, no IO).
 */
import type { TeamAuditEntry } from '../../lib/api/audit/audit-api';
import type { EmployeeRole } from '../../lib/rbac/permissions';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Translation key per recorded event; the wording lives in en/nl.json. */
const EVENT_KEYS: Record<string, string> = {
  'employee.created_with_key': 'TeamActivityEvent_created_with_key',
  'employee.invited': 'TeamActivityEvent_invited',
  'employee.key_regenerated': 'TeamActivityEvent_key_regenerated',
  'employee.invitation_accepted': 'TeamActivityEvent_invitation_accepted',
  'employee.role_changed': 'TeamActivityEvent_role_changed',
  'employee.reactivated': 'TeamActivityEvent_reactivated',
  'employee.deactivated': 'TeamActivityEvent_deactivated',
  'employee.updated': 'TeamActivityEvent_updated',
  'employee.removed': 'TeamActivityEvent_removed',
  'employee.invitation_resent': 'TeamActivityEvent_invitation_resent',
  'team.deleted': 'TeamActivityEvent_team_deleted',
  'team.created': 'TeamActivityEvent_team_created',
  'team.member_added': 'TeamActivityEvent_team_member_added',
  'team.member_removed': 'TeamActivityEvent_team_member_removed',
  'team.role_changed': 'TeamActivityEvent_team_role_changed',
  'team.updated': 'TeamActivityEvent_team_updated',
};

/** Events about a role change: the sentence names the old and new role. */
const ROLE_CHANGE_EVENTS = new Set([
  'employee.role_changed',
  'team.role_changed',
]);

/**
 * Events about a member joining or leaving a team: the subject is the
 * member, and the sentence names the team from `team_name`.
 */
const TEAM_MEMBERSHIP_EVENTS = new Set([
  'team.member_added',
  'team.member_removed',
]);

/**
 * Events whose sentence names a role when the entry carries one (key +
 * `_role`), and the property the role is read from. For the team events the
 * subject is the team, and the role is the one its members inherit (its
 * brand scope).
 */
const ROLE_PROPERTY: Record<string, 'subject_role' | 'role'> = {
  'employee.invitation_resent': 'subject_role',
  'team.deleted': 'role',
  'team.created': 'role',
};

/**
 * The tab is for owners only. `useEmployeeRole()` answers 'owner' until /me
 * has resolved, so a 403 from the endpoint also hides it.
 */
export function showTeamActivityTab(
  role: EmployeeRole | null,
  forbidden: boolean
): boolean {
  return role === 'owner' && !forbidden;
}

/** Plain-language description of what happened to the teammate. */
export function describeTeamEvent(
  entry: Pick<TeamAuditEntry, 'event' | 'properties'>,
  t: Translate,
  roleLabel: (key?: string | null) => string
): string {
  const key = EVENT_KEYS[entry.event];
  if (!key) return t('TeamActivityEvent_other');
  if (ROLE_CHANGE_EVENTS.has(entry.event)) {
    return t(key, {
      old: roleLabel(entry.properties?.old_role) || '-',
      new: roleLabel(entry.properties?.new_role) || '-',
    });
  }
  if (TEAM_MEMBERSHIP_EVENTS.has(entry.event)) {
    return t(key, { team: entry.properties?.team_name || '-' });
  }
  const roleProperty = ROLE_PROPERTY[entry.event];
  const role = roleProperty ? roleLabel(entry.properties?.[roleProperty]) : '';
  if (role) return t(key + '_role', { role });
  return t(key);
}

/** Who made the change: the actor's name, or "System" when there is none. */
export function teamEventActor(
  entry: Pick<TeamAuditEntry, 'actor'>,
  t: Translate
): string {
  if (!entry.actor) return t('TeamActivitySystem');
  return entry.actor.name || t('TeamActivityUnknownPerson');
}

/** Whom the change was about. */
export function teamEventSubject(
  entry: Pick<TeamAuditEntry, 'subject'>,
  t: Translate
): string {
  return entry.subject?.name || t('TeamActivityUnknownPerson');
}

/** A compact, locale-aware timestamp. */
export function formatTeamEventTime(iso: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
